import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import styled, { createGlobalStyle } from 'styled-components'
import { Button, Checkbox, Modal } from 'examkrackers-components'

import { updateChapterSectionsSrqRedDots } from 'services/QuizDiagnosticsService'
import { showToastRoutine } from 'modules/toast/ducks/actions'
import { SEVERITY } from 'utils/toast'

import { ChapterViewSection } from '../chapterView/types'
import { getSrqTimezone } from '../utils/getSrqApiParams'
import { QUIZ_DIAGNOSTICS_OVERLAY_Z_INDEX } from '../utils/quizDiagnosticsOverlayZIndex'
import { patchSharedSrqStatus } from '../utils/srqStatusStore'

const NEVER_TAKEN_BACKGROUND = '#fad3d34f'
const MAX_SECTIONS = 24

type SrqRedDotsSettingsModalProps = {
  open: boolean
  chapterId: string
  tag: string
  chapterOrder: number
  sections: ChapterViewSection[]
  chapterSrqRedDotsVisible: boolean
  courseSrqRedDotsVisible: boolean
  onClose: () => void
  onSaved: (result?: { chapterSrqRedDotsVisible?: boolean }) => void
}

const buildInitialVisibility = ({
  sections,
  chapterSrqRedDotsVisible,
  courseSrqRedDotsVisible
}: {
  sections: ChapterViewSection[]
  chapterSrqRedDotsVisible: boolean
  courseSrqRedDotsVisible: boolean
}): Record<string, boolean> => {
  const isMuted = !chapterSrqRedDotsVisible || !courseSrqRedDotsVisible

  return sections.reduce<Record<string, boolean>>((acc, section) => {
    acc[section.id] = isMuted ? false : section.srqRedDotsVisible ?? true
    return acc
  }, {})
}

const SrqRedDotsSettingsModal = ({
  open,
  chapterId,
  tag,
  chapterOrder,
  sections,
  chapterSrqRedDotsVisible,
  courseSrqRedDotsVisible,
  onClose,
  onSaved
}: SrqRedDotsSettingsModalProps): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isSaving, setIsSaving] = useState(false)
  const [visibilityBySectionId, setVisibilityBySectionId] = useState<
    Record<string, boolean>
  >({})

  const limitedSections = useMemo(
    () => sections.slice(0, MAX_SECTIONS),
    [sections]
  )

  useEffect(() => {
    if (!open) {
      return
    }

    setVisibilityBySectionId(
      buildInitialVisibility({
        sections: limitedSections,
        chapterSrqRedDotsVisible,
        courseSrqRedDotsVisible
      })
    )
  }, [open, limitedSections, chapterSrqRedDotsVisible, courseSrqRedDotsVisible])

  const midpoint = Math.ceil(limitedSections.length / 2)
  const leftColumnSections = limitedSections.slice(0, midpoint)
  const rightColumnSections = limitedSections.slice(midpoint)

  const handleClose = () => {
    if (isSaving) {
      return
    }
    onClose()
  }

  const handleToggleSection = (sectionId: string) => {
    setVisibilityBySectionId(previous => ({
      ...previous,
      [sectionId]: !previous[sectionId]
    }))
  }

  const handleSave = async () => {
    if (!chapterId || isSaving) {
      return
    }

    setIsSaving(true)

    try {
      const response = await updateChapterSectionsSrqRedDots(chapterId, {
        sections: limitedSections.map(section => ({
          student_book_subchapter_id: section.id,
          visible: Boolean(visibilityBySectionId[section.id])
        })),
        timezone: getSrqTimezone()
      })

      const responseData = response?.data as
        | {
            course_srq_red_dots_visible?: boolean
            chapter_srq_red_dots_visible?: boolean
            quizzes_due_today?: number
          }
        | undefined

      patchSharedSrqStatus({
        ...(typeof responseData?.course_srq_red_dots_visible === 'boolean'
          ? { srqRedDotsVisible: responseData.course_srq_red_dots_visible }
          : {}),
        ...(typeof responseData?.quizzes_due_today === 'number'
          ? { quizzesDueToday: responseData.quizzes_due_today }
          : {})
      })

      dispatch(
        showToastRoutine({
          key: 'quizDiagnostics.settingsUpdatedSuccessfully',
          severity: SEVERITY.success
        })
      )

      onSaved({
        chapterSrqRedDotsVisible: responseData?.chapter_srq_red_dots_visible
      })
      onClose()
    } catch (error) {
      console.error('Failed to update chapter section SRQ red dots', error)
    } finally {
      setIsSaving(false)
    }
  }

  const renderSectionRow = (section: ChapterViewSection) => {
    const isChecked = Boolean(visibilityBySectionId[section.id])
    const hasNeverTakenQuiz = section.totalQuizzesTaken === 0
    const sectionCode = `${tag} ${chapterOrder}.${section.sectionOrder}`
    const sectionTitle = section.title
    const sectionLabel = `${sectionCode} ${sectionTitle}`
    const checkboxName = `srq-red-dots-section-${section.id}`

    const handleRowToggle = () => {
      handleToggleSection(section.id)
    }

    const handleLabelKeyDown = (
      event: React.KeyboardEvent<HTMLSpanElement>
    ) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return
      }
      event.preventDefault()
      handleRowToggle()
    }

    return (
      <SectionRow key={section.id} $hasNeverTakenQuiz={hasNeverTakenQuiz}>
        <Checkbox
          name={checkboxName}
          isSelected={isChecked}
          onChange={handleRowToggle}
        />
        <SectionLabel
          role='button'
          tabIndex={0}
          aria-label={sectionLabel}
          onClick={handleRowToggle}
          onKeyDown={handleLabelKeyDown}
        >
          <SectionCode>{sectionCode}</SectionCode>
          <SectionMiniLabel>{sectionTitle}</SectionMiniLabel>
        </SectionLabel>
      </SectionRow>
    )
  }

  return (
    <>
      {open ? <SettingsModalZIndexGlobalStyle /> : null}
      <StyledSettingsModal
        title={t('quizDiagnostics.settingsModal.title')}
        open={open}
        handleClose={handleClose}
        hasBorder
        style={{
          overlay: {
            zIndex: QUIZ_DIAGNOSTICS_OVERLAY_Z_INDEX
          },
          content: {
            zIndex: QUIZ_DIAGNOSTICS_OVERLAY_Z_INDEX + 1,
            width: '783px',
            maxWidth: '783px',
            padding: '30px 80px',
            boxSizing: 'border-box'
          }
        }}
      >
        <ModalBody>
          <Description>{t('quizDiagnostics.settingsModal.body')}</Description>
          <ListHeading>
            {t('quizDiagnostics.settingsModal.showRedDotFor')}
          </ListHeading>
          <Columns>
            <Column>{leftColumnSections.map(renderSectionRow)}</Column>
            <Column>{rightColumnSections.map(renderSectionRow)}</Column>
          </Columns>
          <SaveButtonWrap>
            <SaveButton
              color='secondary'
              variant='contained'
              disabled={isSaving}
              onClick={handleSave}
              aria-label={t('quizDiagnostics.settingsModal.save')}
            >
              {t('quizDiagnostics.settingsModal.save')}
            </SaveButton>
          </SaveButtonWrap>
        </ModalBody>
      </StyledSettingsModal>
    </>
  )
}

export default SrqRedDotsSettingsModal

const SettingsModalZIndexGlobalStyle = createGlobalStyle`
  .Overlay {
    z-index: ${QUIZ_DIAGNOSTICS_OVERLAY_Z_INDEX} !important;
  }

  .Overlay::before {
    z-index: ${QUIZ_DIAGNOSTICS_OVERLAY_Z_INDEX} !important;
  }

  .Modal {
    z-index: ${QUIZ_DIAGNOSTICS_OVERLAY_Z_INDEX + 1} !important;
  }
`

const StyledSettingsModal = styled(Modal)`
  & .Modal {
    width: 783px;
    max-width: 783px;
    padding: 30px 80px;
    box-sizing: border-box;
  }

  /* Modal title is the first content div when no titleIcon is passed */
  & .Modal > div:first-of-type {
    font-family: Arial, sans-serif;
    font-weight: 700;
    font-size: 18px;
    line-height: 1.3;
    text-align: left;
    color: #1a1a1a;
    margin-bottom: 16px;
  }
`

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
`

const Description = styled.p`
  margin: 0;
  font-family: Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 1.45;
  text-align: left;
  color: #444444;
`

const ListHeading = styled.p`
  margin: 16px 0 8px;
  font-family: Arial, sans-serif;
  font-size: 18px;
  font-weight: 700;
  font-style: normal;
  text-align: left;
  color: #444444;
`

const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 24px;
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const SectionRow = styled.div<{ $hasNeverTakenQuiz: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  background-color: ${({ $hasNeverTakenQuiz }) =>
    $hasNeverTakenQuiz ? NEVER_TAKEN_BACKGROUND : 'transparent'};

  .exam-checkbox {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`

const SectionLabel = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
  flex: 1;
  min-width: 0;
  cursor: pointer;
`

const SectionCode = styled.span`
  font-family: Arial, sans-serif;
  font-size: 16px;
  font-weight: 400;
  font-style: normal;
  line-height: 1.3;
  color: #444444;
`

const SectionMiniLabel = styled.span`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: 400;
  font-style: normal;
  line-height: 1.3;
  color: #444444;
`

const SaveButtonWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`

const SaveButton = styled(Button)`
  && {
    width: 74px;
    min-width: 74px;
    max-width: 74px;
    height: 30px;
    min-height: 30px;
    max-height: 30px;
    border-radius: 4px;
    box-sizing: border-box;
    padding-top: 0;
    padding-bottom: 0;
    padding-left: 0;
    padding-right: 0;
    line-height: 30px;
  }
`
